import {
  EVENT_CATEGORIES,
  EVENTS,
  useActionsTrack,
} from "@/_old/features/analytics";
import { useAnalytics } from "@/_old/features/analytics/contexts/segment";
import { Member } from "@/company/members/types";
import { MemberInput } from "@/core/components/layout/member-input";
import { Label } from "@/core/components/ui/label";
import { useAuth } from "@/core/components/auth/store/auth";
import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

interface MemberFilterProps {
  location: string;
  onSubmit?: boolean;
  onChange?: (value: Member[] | null) => void;
}

export function MemberFilter({
  location,
  onSubmit,
  onChange,
}: MemberFilterProps) {
  const [translate] = useTranslation(["core"]);
  const { filters, setFilters } = useDashboardFilters();
  const { onActionClick } = useActionsTrack();
  const { track } = useAnalytics();
  const { userInfo } = useAuth();
  const companyTypeAndMembersSizeSent = useRef(false);

  const value = useMemo(() => {
    return filters.users || null;
  }, [filters.users]);

  const handleChange = useCallback(
    (value: Member[] | null) => {
      onActionClick("INSIGHTS_USE_FILTER_MEMBERS", "DASHBOARD");

      const users: Member[] = value || [];

      if (onSubmit) {
        onChange?.(users);
      } else {
        setFilters({ users });
      }
    },
    [onChange, onSubmit, setFilters, onActionClick],
  );

  const onMembersLoaded = useCallback(
    (total: number) => {
      if (companyTypeAndMembersSizeSent.current || !userInfo?.league) return;
      companyTypeAndMembersSizeSent.current = true;
      track(EVENTS.COMPANY_TYPE_AND_MEMBERS_SIZE, {
        category: EVENT_CATEGORIES.VIEW,
        value: {
          size: total,
          classification: userInfo.league.classification ?? undefined,
        },
      });
    },
    [track, userInfo?.league],
  );

  return (
    <div className="flex flex-col">
      <Label htmlFor={`${location}-member-filter`} className="mb-2">
        {translate("members-filter-label") as string}
      </Label>

      <MemberInput
        inputId={`${location}-member-filter`}
        defaultValue={value}
        hasEmptyValue={true}
        placeholder={translate("members-filter-placeholder") as string}
        onChange={handleChange}
        {...(location === "dashboard" ? { onMembersLoaded } : {})}
      />
    </div>
  );
}
